import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from './api.service';

/** Row returned when listing Stripe Connect accounts (backend must call Stripe with the secret key). */
export interface StripeConnectAccountRow {
  id: string;
  stripe_account_id?: string;
  display_name?: string;
  email?: string;
}

/** Location row tied to a Connect account (app hub location and/or Stripe Terminal location). */
export interface StripeConnectLocationRow {
  id: string;
  display_name?: string;
  branch?: string;
  name?: string;
  stripe_account_id?: string;
  /** Hub/app location id when the option comes from profile locations. */
  app_location_id?: string;
  /** Stripe Terminal location id (`tml_...`) when known. */
  terminal_location_id?: string;
}

function normalizeAccountRow(raw: any): StripeConnectAccountRow | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const row = raw as Record<string, any>;
  const id = String(row['id'] ?? row['stripe_account_id'] ?? row['stripeAccountId'] ?? '').trim();
  const stripeAccountId = String(row['stripe_account_id'] ?? row['stripeAccountId'] ?? id).trim();
  if (!id && !stripeAccountId) return null;
  return {
    id: id || stripeAccountId,
    stripe_account_id: stripeAccountId || id,
    display_name: typeof row['display_name'] === 'string' ? row['display_name'] : undefined,
    email: typeof row['email'] === 'string' ? row['email'] : undefined,
  };
}

function normalizeAccountList(data: any): StripeConnectAccountRow[] {
  if (!data) return [];
  if (Array.isArray(data)) {
    return data.map(normalizeAccountRow).filter((a): a is StripeConnectAccountRow => !!a);
  }
  const d = data as Record<string, any>;
  const inner = d['accounts'] ?? d['data'];
  if (Array.isArray(inner)) {
    return inner.map(normalizeAccountRow).filter((a): a is StripeConnectAccountRow => !!a);
  }
  return [];
}

function normalizeLocationList(data: any): StripeConnectLocationRow[] {
  if (!data) return [];
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as any).locations)
      ? (data as any).locations
      : Array.isArray((data as any).terminal_locations)
        ? (data as any).terminal_locations
        : Array.isArray((data as any).data)
          ? (data as any).data
          : null;
  if (!rows) {
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const nested = (data as any).data;
      if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
        return normalizeLocationList(nested);
      }
    }
    return [];
  }
  return rows
    .map((raw: any) => {
      if (!raw || typeof raw !== 'object') return null;
      const id = String(raw.id ?? raw.terminal_location_id ?? '').trim();
      if (!id) return null;
      return {
        id,
        terminal_location_id: id.startsWith('tml_') ? id : undefined,
        display_name:
          typeof raw.display_name === 'string'
            ? raw.display_name
            : typeof raw.displayName === 'string'
              ? raw.displayName
              : undefined,
        branch: typeof raw.branch === 'string' ? raw.branch : undefined,
        name: typeof raw.name === 'string' ? raw.name : undefined,
        stripe_account_id:
          typeof raw.stripe_account_id === 'string'
            ? raw.stripe_account_id
            : typeof raw.stripeAccountId === 'string'
              ? raw.stripeAccountId
              : undefined,
      } as StripeConnectLocationRow;
    })
    .filter((l: StripeConnectLocationRow | null): l is StripeConnectLocationRow => !!l);
}

/**
 * Loads Stripe Connect account / location options via your backend API.
 * The browser must never use the Stripe **secret** key; only the server should call Stripe's REST API.
 *
 * Expected endpoints (relative to `environment.baseUrl` / `apiVersion`):
 * - `GET stripe/connect/accounts` → `{ status, data: { accounts: [...] } }` or `{ status, data: [...] }`
 * - `GET location/terminal/locations` with `{ stripe_account_id }` → Stripe Terminal locations for that account
 * - `PUT location/edit-location/:locationId` with `{ stripe_account_id, terminal_location_id }`
 */
@Injectable({ providedIn: 'root' })
export class StripeConnectSettingsService {
  constructor(private api: ApiService) {}

  listConnectAccounts(): Observable<StripeConnectAccountRow[]> {
    return this.api.get<any>('stripe/connect/accounts').pipe(
      map(res => {
        if (!res?.status || res.data == null) return [];
        return normalizeAccountList(res.data);
      }),
      catchError(() => of([]))
    );
  }

  listLocationsForAccount(accountId: string): Observable<StripeConnectLocationRow[]> {
    const stripeAccountId = String(accountId || '').trim();
    if (!stripeAccountId) return of([]);
    // Backend validates query (or body); prefer query — browsers often drop GET bodies.
    return this.api
      .get<any>('location/terminal/locations', { stripe_account_id: stripeAccountId })
      .pipe(
        map((res: any) => {
          if (res?.status === false) return [];
          // Shop returns Stripe list shape `{ object:'list', data:[...] }` (not `{ status, data }`).
          // If wrapped as `{ status, data: { data: [...] } }`, unwrap both layers.
          let root: any = res;
          if (res && typeof res === 'object' && res.data != null && res.object !== 'list') {
            root = res.data;
          }
          return normalizeLocationList(root);
        }),
        catchError(() => of([]))
      );
  }

  saveLocationPaymentSettings(
    locationId: string,
    payload: { stripe_account_id: string; terminal_location_id: string }
  ): Observable<boolean> {
    const id = String(locationId || '').trim();
    const terminalLocationId = String(payload.terminal_location_id || '').trim();
    if (!id || !payload.stripe_account_id || !/^tml_[a-zA-Z0-9]+$/.test(terminalLocationId)) {
      return of(false);
    }
    return this.api
      .put(`location/edit-location/${encodeURIComponent(id)}`, {
        stripe_account_id: payload.stripe_account_id,
        terminal_location_id: terminalLocationId,
      })
      .pipe(
        map(r => !!r?.status),
        catchError(() => of(false))
      );
  }
}
